<template>
  <div>
    <p><b>Añadir Objetivo:</b></p>

    <ValidationObserver ref="ObjetiveForm">
      <ValidationProvider
        v-slot="{ errors }"
        rules="required"
        name="la descripción"
      >
        <CustomTextarea
          v-model="model.description"
          label="Concepto:"
          class="mb-4"
          required="required"
          :maxlength="500"
          custom-error-msg="Campo es requerido"
          :error="errors[0]"
        />
      </ValidationProvider>

      <div class="row">
        <div class="col-3">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            name="el instrumento"
          >
            <CustomDropDown
              v-model="model.instrument"
              label="Instrumento:"
              :options="instrumentOptions"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              :error="errors[0]"
              custom-error-msg="Campo es requerido"
            />
          </ValidationProvider>
        </div>
        <div class="col-3">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            name="la adecuación"
          >
            <CustomDropDown
              v-model="model.adequacy"
              label="Adecuación:"
              :options="adequacyOptions"
              fieldkey="id"
              fieldname="spaShortDescription"
              placeholder="Seleccione"
              :error="errors[0]"
              custom-error-msg="Campo es requerido"
            />
          </ValidationProvider>
        </div>
        <div class="col-6" v-if="!!model.adequacy" style="padding-top: 27px;">
          <div class="row">
            <div class="col-3">
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                ref="measure1Amt"
              >
                <CustomInput
                  v-model="model.measure1Amt"
                  type="number"
                  :error="errors[0]"
                  custom-error-msg="Campo es requerido"
                />
              </ValidationProvider>
            </div>

            <div class="d-flex align-items-center">
              <div v-if="isAttempsAdequacy">de</div>
              <div v-else>%</div>
            </div>

            <div v-if="isAttempsAdequacy" class="col-3">
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                ref="measure2Amt"
              >
                <CustomInput
                  v-model="model.measure2Amt"
                  type="number"
                  :error="errors[0]"
                  custom-error-msg="Campo es requerido"
                />
              </ValidationProvider>
            </div>
          </div>
          <Error
            v-if="!!customErrorMsg"
            class="mt-2"
            variant="no-border"
            :message="customErrorMsg"
          />
        </div>
      </div>
    </ValidationObserver>

    <Previewer title="Previsualizar Objetivo:" :text="preview" />

    <div class="objetive-actions__container">
      <ActionButton
        i="fa fa-times-circle"
        class="mr-2"
        variant="outline-danger"
        @click="$emit('cancel')"
      >
        Cancelar
      </ActionButton>
      <ActionButton i="fas fa-save" variant="success" @click="save">
        Guardar
      </ActionButton>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import GoalMeasures from '@/utils/constants/pei/peiGoalMeasures';
import CustomTextarea from '@/components/common/CustomTextArea';
import ActionButton from '@/components/common/ActionButton';
import CustomInput from '@/components/common/CustomInput';
import CustomDropDown from '@/components/common/CustomDropDown';
import Error from '@/components/common/Error';
import Previewer from './common/Previewer.vue';
import { BuildObjetiveDescription } from './common/Helper.js';

export default {
  name: 'ObjetiveForm',
  components: {
    CustomTextarea,
    ActionButton,
    ValidationProvider,
    ValidationObserver,
    CustomInput,
    CustomDropDown,
    Error,
    Previewer,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        description: '',
        instrument: null,
        adequacy: null,
        measure1Amt: null,
        measure2Amt: null,
      }),
    },
    instrumentOptions: {
      type: Array,
      default: () => [],
    },
    adequacyOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      model: {
        description: '',
        instrument: null,
        adequacy: null,
        measure1Amt: null,
        measure2Amt: null,
        // ver el comentario en save() sobre estos dos.
        descriptionOverride: null,
        descriptionAiGenerationLogId: null,
      },
    };
  },
  methods: {
    async save() {
      const isValid =
        (await this.$refs.ObjetiveForm.validate()) && !this.customErrorMsg;
      if (!isValid) return isValid;

      // "editar antes de usar" (ver aiSuggestionEditMixin.js/SubAreaGoalCallout.vue-
      // >applyAiObjectiveSuggestion): mismo criterio que GoalForm.vue -- si el especialista
      // editó a mano el texto de la tarjeta de sugerencia, descriptionOverride reemplaza a
      // BuildObjetiveDescription; si no (incl. el alta manual normal, que nunca lo setea), el
      // cálculo es idéntico al de antes. descriptionAiGenerationLogId solo viaja sin editar
      // (ver PeiEngine.AddPeiProgramServiceObjective/RegisterAppliedAiField).
      const payload = {
        description: this.model.descriptionOverride || BuildObjetiveDescription(this.model),
        descriptionAiGenerationLogId: this.model.descriptionOverride
          ? null
          : (this.model.descriptionAiGenerationLogId || null),
        instrumentId: parseInt(this.model.instrument.id),
        adequacyId: parseInt(this.model.adequacy.id),
        measure1Amt: parseInt(this.model.measure1Amt),
        measure2Amt: parseInt(this.model.measure2Amt),
      };

      this.$emit('save', payload);
      this.$refs.ObjetiveForm.reset();

      return isValid;
    },
  },
  computed: {
    preview() {
      return BuildObjetiveDescription(this.model);
    },
    isAttempsAdequacy() {
      return this.model.adequacy?.id == GoalMeasures.attempts;
    },
    customErrorMsg() {
      if (
        !this.isAttempsAdequacy &&
        this.model.measure1Amt &&
        (this.model.measure1Amt < 1 || this.model.measure1Amt > 100)
      )
        return 'El porcentaje debe ser mayor o igual a 1 y menor o igual a 100';

      if (
        this.isAttempsAdequacy &&
        this.model.measure1Amt &&
        this.model.measure2Amt &&
        this.model.measure1Amt > this.model.measure2Amt
      )
        return 'El valor de los intentos no puede ser mayor que el total de intentos';
    },
  },
  watch: {
    value: {
      deep: true,
      // immediate: true -- AG-05 ("Objetivo con Asistente IA") necesita poder pre-llenar
      // este formulario (concepto/instrumento/medida sugeridos) DESDE el padre justo antes
      // de montarlo, no solo recibir cambios en vivo mientras ya está montado. Sin
      // immediate, un ObjetiveForm recién creado ignoraba el valor inicial de "value" y
      // arrancaba en blanco pase lo que pase -- no cambia el comportamiento existente,
      // porque el flujo manual ya arranca con "value" en blanco.
      immediate: true,
      handler(newValue) {
        this.model.description = newValue.description;
        this.model.instrument = newValue.instrument;
        this.model.adequacy = newValue.adequacy;
        this.model.measure1Amt = newValue.measure1Amt;
        this.model.measure2Amt = newValue.measure2Amt;
        // "editar antes de usar" -- ver el comentario en save().
        this.model.descriptionOverride = newValue.descriptionOverride || null;
        this.model.descriptionAiGenerationLogId = newValue.descriptionAiGenerationLogId || null;
      },
    },
    model: {
      deep: true,
      handler(newValue) {
        this.$emit('input', newValue);
      },
    },
  },
};
</script>

<style>
.objetive-actions__container {
  display: flex;
  justify-content: flex-end;
}
</style>
